/**
 * Pure H OS · Phase D Wave 4 · Product Workspace.
 * Cross-functional unified SKU view — R&D + Ops + Marketing + Finance.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { PRODUCTS_HUB, ProductLaunchStatus } from "../mocks/productsHubData";

const STATUS_LABEL: Record<ProductLaunchStatus, string> = {
  discovery: "Discovery", reformulation: "Reformulation",
  "pre-launch": "Pre-launch", live: "Live", killed: "Killed",
};
function statusSeverity(s: ProductLaunchStatus): "low" | "medium" | "high" {
  if (s === "live" || s === "pre-launch") return "low";
  if (s === "discovery" || s === "reformulation") return "medium";
  return "high";
}

// 8-stage fixed R&D → Launch gate. Each product's `launchStatus` maps to
// the "current stage" — everything before is treated as completed, the
// current step is in-progress, everything after is pending. `killed`
// visualises as blocked on the current-inferred stage.
const STAGE_KEYS = [
  "idea",
  "formula",
  "lab_session",
  "blind_test",
  "reformulation",
  "pilot_production",
  "packaging",
  "launch_ready",
] as const;
const STAGE_LABEL: Record<(typeof STAGE_KEYS)[number], string> = {
  idea:             "Idea",
  formula:          "Formula",
  lab_session:      "Lab Session",
  blind_test:       "Blind Testing",
  reformulation:    "Reformulation",
  pilot_production: "Pilot Production",
  packaging:        "Packaging",
  launch_ready:     "Launch Ready",
};
function stageIndexFor(status: ProductLaunchStatus, blindScore: number): number {
  switch (status) {
    case "discovery":     return 1; // formula stage in progress
    case "reformulation": return 4; // reformulation stage in progress
    case "pre-launch":    return blindScore >= 8 ? 6 : 5; // packaging or pilot
    case "live":          return 7; // launch ready done
    case "killed":        return 4; // blocked at reformulation
    default:              return 0;
  }
}

type Tab = "all" | ProductLaunchStatus;

export default function ProductWorkspaceHub() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => (tab === "all" ? PRODUCTS_HUB : PRODUCTS_HUB.filter((p) => p.launchStatus === tab)), [tab]);
  const current = openId ? PRODUCTS_HUB.find((p) => p.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="product-workspace-page">
      <Card testId="ph-filters">
        <TabBar<Tab>
          testId="ph-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",            label: `All ${PRODUCTS_HUB.length}` },
            { key: "live",           label: "Live" },
            { key: "pre-launch",     label: "Pre-launch" },
            { key: "discovery",      label: "Discovery" },
            { key: "reformulation",  label: "Reformulation" },
            { key: "killed",         label: "Killed" },
          ]}
        />
      </Card>

      <Card padded={false} testId="ph-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Product Workspace" title={`${filtered.length} SKU${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Cross-functional per-SKU rollup (R&D + Ops + Marketing + Finance). For the customer-facing catalogue see Commerce · Products; for formulation see R&D Bench.</div>
        </div>
        <div className="p-2">
          {filtered.map((p) => (
            <ListRow key={p.id} testId={`ph-${p.id}`}
              onClick={() => setOpenId(p.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{p.sku}</span>
                  <span>{p.name}</span>
                </span>
              }
              sublabel={<>{p.category} · {p.leadPerfumer} · EGP {p.monthlyRevenueEGP.toLocaleString()} MRR{p.monthlyRevenueEGP > 0 ? ` · ${p.marginPct.toFixed(1)}% margin` : ""} · panel {p.blindPanelScore.toFixed(1)}</>}
              trailing={<SeverityBadge severity={statusSeverity(p.launchStatus)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="ph-drawer" title={current ? current.name : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${current.sku} · ${STATUS_LABEL[current.launchStatus]}`} title={`${current.category} · ${current.leadPerfumer}`} />
            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>{current.notes}</div>
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Monthly revenue</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.monthlyRevenueEGP.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Units shipped</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.unitsShipped}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Margin</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.monthlyRevenueEGP > 0 ? `${current.marginPct.toFixed(1)}%` : "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Blind panel</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.blindPanelScore.toFixed(1)} / 10</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Active campaigns</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.activeCampaigns}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Status</div><div className="mt-2"><SeverityBadge severity={statusSeverity(current.launchStatus)} /></div></div>
            </div>

            {/* Product Stage-Gate — 8-fixed-stage progression */}
            {(() => {
              const currentIdx = stageIndexFor(current.launchStatus, current.blindPanelScore);
              const isKilled = current.launchStatus === "killed";
              const isLive = current.launchStatus === "live";
              return (
                <div data-testid={`ph-stagegate-${current.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Stage gate · {isLive ? "8/8" : isKilled ? `${currentIdx}/8 · blocked` : `${currentIdx}/8`}</div>
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {STAGE_KEYS.map((k, i) => {
                      const done = i < currentIdx || isLive;
                      const active = !isLive && i === currentIdx;
                      const blocked = isKilled && i === currentIdx;
                      const bg = done ? "var(--ph-accent)" : active ? (blocked ? "var(--ph-danger)" : "var(--ph-warning)") : "var(--ph-surface-well)";
                      const fg = done || active ? "#fff" : "var(--ph-muted)";
                      return (
                        <div
                          key={k}
                          data-testid={`ph-stage-${k}`}
                          className="rounded p-2 text-[10px] uppercase tracking-[0.14em]"
                          style={{ background: bg, color: fg, border: "1px solid var(--ph-border-subtle)" }}
                        >
                          <div className="tabular-nums" style={{ fontWeight: 600 }}>{i + 1}.</div>
                          <div style={{ fontSize: "10px" }}>{STAGE_LABEL[k]}</div>
                          <div className="mt-1 text-[9px]" style={{ opacity: 0.85 }}>
                            {done ? "done" : active ? (blocked ? "blocked" : "in progress") : "pending"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Drawer>
    </div>
  );
}
